import { useMemo } from 'react';
import { DEFAULT_MAIN_COLUMNS, MAIN_COLUMN_KEYS } from '@/entities/conversations';
import { useExpertReports, useExpertReportsBlocksTree } from '@/entities/expert-report';
import { collectLeafBlockIds } from '../DialogsListAttributesSettings/expertReportItems';
import { useAttributesSettings } from '../DialogsListAttributesSettings';
import { ATTRIBUTE_COLUMN_PREFIX, EXPERT_REPORT_STANDARD_COLUMNS } from './constants';

/**
 * Загружает все источники данных для колонок таблицы диалогов и вычисляет дефолтную видимость
 * всех динамических колонок.
 *
 * Сами колонки строит `useDialogDataGridColumns` — отдельно и позже: их вид зависит от того, чем
 * оценивается блок, а это известно только по видимым заключениям, то есть уже после того, как
 * состояние видимости разложено по этой дефолтной видимости.
 *
 * Не знает о стейте и персистенции — только о данных.
 */
export const useDialogColumnsData = () => {
  const { attributes: allAttributes, isLoadingAttributes } = useAttributesSettings();

  const { data: reportsData } = useExpertReports({ isDeleted: false, limit: 100 });
  const allReports = useMemo(() => reportsData?.items ?? [], [reportsData?.items]);
  const isReportsLoaded = reportsData !== undefined;

  const allReportIds = useMemo(() => allReports.map((r) => r.id), [allReports]);
  const blocksByReportId = useExpertReportsBlocksTree(allReportIds);

  /**
   * Дефолтная видимость: основные колонки видимы по DEFAULT_MAIN_COLUMNS,
   * остальные динамические колонки скрыты при первом открытии.
   * Включает основные колонки, атрибуты, стандартные колонки отчётов и блоки всех отчётов.
   * Обновляется при подгрузке новых блоков.
   */
  const defaultVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};

    // Основные колонки: видимы только те, что в DEFAULT_MAIN_COLUMNS
    MAIN_COLUMN_KEYS.forEach((key) => {
      vis[key] = DEFAULT_MAIN_COLUMNS.includes(key);
    });

    allAttributes.forEach((attr) => {
      vis[`${ATTRIBUTE_COLUMN_PREFIX}${attr.path}`] = false;
    });

    allReports.forEach((report) => {
      EXPERT_REPORT_STANDARD_COLUMNS.forEach((col) => {
        vis[`expertReport_${report.id}_${col}`] = false;
      });

      const leafBlockIds = (blocksByReportId.get(report.id) ?? []).flatMap(collectLeafBlockIds);
      leafBlockIds.forEach((blockId) => {
        vis[`expertReport_${report.id}_block_${blockId}`] = false;
      });
    });

    return vis;
  }, [allAttributes, allReports, blocksByReportId]);

  return {
    defaultVisibility,
    allAttributes,
    allReports,
    blocksByReportId,
    isLoading: isLoadingAttributes || !isReportsLoaded,
  };
};
